import { memo, useRef, useState } from 'react';
import type { RefObject } from 'react';
import { Box, Tab, Tabs } from '@mui/material';

import { PLAN_COLORS } from '../constants';
import { useCompactMetricsVisible } from '../hooks/useCompactMetricsVisible';
import type { PlanTab } from '../hooks/useNurturingPlan';
import type { GeneratedPlan, PlanConfig } from '../types';
import { METRIC_GRID_COMPACT_HEIGHT } from '../utils/metricGridLayout';
import { DailyViewHorizonBar } from './DailyViewHorizonBar';
import { DailyViewTab } from './DailyViewTab';
import { EmailAddressesTab } from './EmailAddressesTab';
import { CompactMetricRow, ExpandedMetricGrid } from './MetricGrid';
import { LoadScheduleTab } from './LoadScheduleTab';
import { PhasedPlanTab } from './PhasedPlanTab';
import { SummaryTab } from './SummaryTab';

const TABS: Array<{ id: PlanTab; label: string }> = [
  { id: 'summary', label: 'Summary' },
  { id: 'phases', label: 'Phased Plan' },
  { id: 'email', label: 'Email Addresses' },
  { id: 'loading', label: 'Load Schedule' },
  { id: 'daily', label: 'Daily View' },
];

interface PlanOutputPanelProps {
  plan: GeneratedPlan;
  config: PlanConfig;
  activeTab: PlanTab;
  onTabChange: (tab: PlanTab) => void;
  openBatches: Record<string, boolean>;
  toggleBatch: (key: string) => void;
  scrollContainerRef: RefObject<HTMLElement | null>;
}

/**
 * Memoized plan output — avoids re-rendering heavy tables while typing in the config panel.
 */
export const PlanOutputPanel = memo(function PlanOutputPanel({
  plan,
  config,
  activeTab,
  onTabChange,
  openBatches,
  toggleBatch,
  scrollContainerRef,
}: PlanOutputPanelProps) {
  const expandedRef = useRef<HTMLDivElement>(null);
  const showCompactMetrics = useCompactMetricsVisible(scrollContainerRef, expandedRef);
  const [dailyHorizon, setDailyHorizon] = useState(120);
  const showDailyHorizonBar =
    activeTab === 'daily' && (plan.dailyPlan?.days?.length ?? 0) > 0;

  return (
    <>
      <Box ref={expandedRef}>
        <ExpandedMetricGrid summary={plan.summary} config={config} />
      </Box>

      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 2,
          bgcolor: 'background.paper',
          boxShadow: showCompactMetrics ? 1 : 0,
        }}
      >
        {showCompactMetrics ? (
          <Box
            sx={{
              height: METRIC_GRID_COMPACT_HEIGHT,
              bgcolor: 'background.paper',
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
          >
            <CompactMetricRow summary={plan.summary} config={config} />
          </Box>
        ) : null}

        <Tabs
          value={activeTab}
          onChange={(_, v: PlanTab) => onTabChange(v)}
          sx={{
            borderBottom: showDailyHorizonBar ? 'none' : '2px solid',
            borderColor: 'divider',
            minHeight: 0,
            '& .MuiTab-root': {
              textTransform: 'none',
              fontSize: 13,
              fontWeight: 500,
              minHeight: 0,
              py: 1,
            },
            '& .Mui-selected': { color: `${PLAN_COLORS.orange} !important`, fontWeight: 600 },
            '& .MuiTabs-indicator': { bgcolor: PLAN_COLORS.orange },
          }}
        >
          {TABS.map((t) => (
            <Tab key={t.id} value={t.id} label={t.label} />
          ))}
        </Tabs>

        {showDailyHorizonBar && plan.dailyPlan ? (
          <DailyViewHorizonBar
            horizon={dailyHorizon}
            onHorizonChange={setDailyHorizon}
            dailyPlan={plan.dailyPlan}
          />
        ) : null}
      </Box>

      <Box sx={{ pt: 2.5 }}>
        {activeTab === 'summary' && <SummaryTab summary={plan.summary} />}
        {activeTab === 'phases' && <PhasedPlanTab phases={plan.phases} config={config} />}
        {activeTab === 'email' && (
          <EmailAddressesTab
            addrEvents={plan.addrEvents}
            starts={plan.starts}
            config={config}
            plan={plan}
          />
        )}
        {activeTab === 'daily' && (
          <DailyViewTab
            dailyPlan={plan.dailyPlan}
            config={config}
            dailyBatch={plan.summary.dailyBatch ?? plan.dailyBatch}
            horizon={dailyHorizon}
          />
        )}
        {activeTab === 'loading' && (
          <LoadScheduleTab
            batches={plan.batches}
            config={config}
            openBatches={openBatches}
            toggleBatch={toggleBatch}
          />
        )}
      </Box>
    </>
  );
}, (prev, next) => {
  return (
    prev.plan === next.plan &&
    prev.activeTab === next.activeTab &&
    prev.openBatches === next.openBatches &&
    prev.onTabChange === next.onTabChange &&
    prev.toggleBatch === next.toggleBatch
  );
});
